import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Clock, Briefcase, Users, CheckCircle, TrendingUp, AlertTriangle,
  Flame, BarChart3, Activity, ArrowUpRight, ArrowDownRight, X, ChevronRight,
  Calendar, DollarSign, Sparkles, Sun, Sunset, Moon, Pencil,
  ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const CHART_COLORS = [
  'hsl(221, 83%, 53%)', 'hsl(142, 71%, 45%)', 'hsl(262, 83%, 58%)',
  'hsl(38, 92%, 50%)', 'hsl(0, 84%, 60%)', 'hsl(190, 90%, 50%)'
];

const Dashboard = () => {
  const { api, user, isAdmin, isSuperAdmin, canViewReports } = useAuth();
  const [stats, setStats] = useState(null);
  const [utilization, setUtilization] = useState(null);
  const [projects, setProjects] = useState([]);
  const [capacityData, setCapacityData] = useState(null);
  const [burnData, setBurnData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [allUsers, setAllUsers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberProjects, setMemberProjects] = useState([]);
  const [memberProjectHours, setMemberProjectHours] = useState({});
  const [memberMonthlyProjectHours, setMemberMonthlyProjectHours] = useState({});
  const [editAllocDialog, setEditAllocDialog] = useState(null); // { project, currentAlloc, newHours }
  const [editAllocSaving, setEditAllocSaving] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchProjects();
    if (isSuperAdmin || isAdmin) fetchAllUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'capacity' && !capacityData && canViewReports) fetchCapacityData();
    if (activeTab === 'burnrate' && !burnData && canViewReports) fetchBurnData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchDashboardData = async (memberId = null) => {
    try {
      const userParam = memberId ? `&user_id=${memberId}` : '';
      const [statsRes, utilRes] = await Promise.all([
        api.get(`/dashboard/stats${memberId ? `?user_id=${memberId}` : ''}`),
        api.get(`/dashboard/utilization?period=week${userParam}`),
      ]);
      setStats(statsRes.data);
      setUtilization(utilRes.data);
    } catch (err) {
      console.error('Failed to fetch dashboard data', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await api.get('/users?is_active=true');
      setAllUsers(
        res.data
          .filter(u => u.id !== user?.id)
          .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
      );
    } catch (err) { console.error(err); }
  };

  const fetchMemberProjects = async (memberId) => {
    try {
      const now = new Date();
      const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      const monthEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const [projectsRes, entriesRes, monthEntriesRes] = await Promise.all([
        api.get('/projects?status=active'),
        api.get(`/time-entries?user_id=${memberId}`),
        api.get(`/time-entries?user_id=${memberId}&date_from=${monthStart}&date_to=${monthEnd}`),
      ]);
      const assigned = projectsRes.data.filter(p =>
        p.allocations?.some(a => a.user_id === memberId)
      );
      // All-time hours per project
      const hoursMap = {};
      for (const e of entriesRes.data) {
        hoursMap[e.project_id] = (hoursMap[e.project_id] || 0) + (e.hours || 0);
      }
      // This month's hours per project (for retainer projects)
      const monthlyHoursMap = {};
      for (const e of monthEntriesRes.data) {
        monthlyHoursMap[e.project_id] = (monthlyHoursMap[e.project_id] || 0) + (e.hours || 0);
      }
      setMemberProjects(assigned);
      setMemberProjectHours(hoursMap);
      setMemberMonthlyProjectHours(monthlyHoursMap);
    } catch (err) { console.error(err); }
  };

  const handleMemberSelect = (memberId) => {
    if (!memberId || memberId === 'all') {
      setSelectedMember(null);
      setMemberProjects([]);
      setMemberProjectHours({});
      setLoading(true);
      fetchDashboardData(null);
      fetchProjects();
      return;
    }
    const member = allUsers.find(u => u.id === memberId);
    setSelectedMember(member);
    setLoading(true);
    fetchDashboardData(memberId);
    fetchMemberProjects(memberId);
  };

  const saveAllocation = async () => {
    if (!editAllocDialog) return;
    const hours = parseFloat(editAllocDialog.newHours);
    if (isNaN(hours) || hours <= 0) { toast.error('Enter a valid number of hours'); return; }
    setEditAllocSaving(true);
    try {
      const { project } = editAllocDialog;
      const updatedAllocations = (project.allocations || []).map(a =>
        a.user_id === selectedMember.id ? { ...a, allocated_hours: hours } : a
      );
      await api.put(`/projects/${project.id}`, { allocations: updatedAllocations });
      toast.success('Allocation updated');
      setEditAllocDialog(null);
      fetchMemberProjects(selectedMember.id);
    } catch (err) {
      toast.error('Failed to update allocation');
    } finally {
      setEditAllocSaving(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await api.get('/projects?status=active');
      let projectList = res.data;
      if (!isAdmin && !isSuperAdmin) {
        projectList = projectList.filter(p =>
          p.allocations?.some(a => a.user_id === user?.id)
        );
      }
      setProjects(projectList.slice(0, 5));
    } catch (err) { console.error(err); }
  };

  const fetchCapacityData = async () => {
    try {
      const res = await api.get('/dashboard/capacity-planning');
      setCapacityData(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchBurnData = async () => {
    try {
      const res = await api.get('/dashboard/burn-rate');
      setBurnData(res.data);
    } catch (err) { console.error(err); }
  };

  const dailyData = utilization?.daily_hours
    ? Object.entries(utilization.daily_hours).map(([date, data]) => ({
        date: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }),
        total: parseFloat((data.total || 0).toFixed(2)),
        billable: parseFloat((data.billable || 0).toFixed(2)),
      }))
    : [];

  const projectData = utilization?.project_breakdown?.slice(0, 5).map((p, i) => ({
    name: p.project_name,
    hours: parseFloat((p.hours || 0).toFixed(2)),
    fill: CHART_COLORS[i % CHART_COLORS.length],
  })) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Welcome Banner */}
      {(() => {
        const hour = new Date().getHours();
        const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
        const TimeIcon = hour < 12 ? Sun : hour < 17 ? Sunset : Moon;
        const gradients = {
          morning:   'from-amber-500/10 via-orange-400/5 to-yellow-300/10',
          afternoon: 'from-blue-600/10 via-indigo-500/5 to-violet-400/10',
          evening:   'from-violet-600/10 via-purple-500/5 to-indigo-400/10',
        };
        const iconColors = {
          morning:   'text-amber-500',
          afternoon: 'text-blue-500',
          evening:   'text-violet-500',
        };
        const dotColors = {
          morning:   'bg-amber-400',
          afternoon: 'bg-blue-500',
          evening:   'bg-violet-500',
        };
        const today = new Date();
        const dayName = today.toLocaleDateString('en-US', { weekday: 'long' });
        const dateLabel = today.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        return (
          <div className={`relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br ${gradients[timeOfDay]} p-6`}>
            {/* decorative blobs */}
            <div className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full bg-primary/5 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-primary/5 blur-2xl" />

            <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              {/* Left: greeting */}
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-background/70 backdrop-blur-sm border border-border flex items-center justify-center shadow-sm">
                  <TimeIcon className={`w-7 h-7 ${iconColors[timeOfDay]}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`inline-block w-2 h-2 rounded-full ${dotColors[timeOfDay]} animate-pulse`} />
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">{dayName}, {dateLabel}</span>
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="dashboard-greeting">
                    Good {timeOfDay},{' '}
                    <span className="bg-gradient-to-r from-primary to-violet-500 bg-clip-text text-transparent">
                      {selectedMember ? `${selectedMember.first_name} ${selectedMember.last_name}` : user?.first_name}
                    </span>
                  </h1>
                  <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-primary/60" />
                    {selectedMember
                      ? `Viewing analytics for ${selectedMember.first_name} ${selectedMember.last_name}`
                      : `Week of ${stats?.week_start} — Track your time and stay productive`}
                  </p>
                </div>
              </div>

              {/* Right: member filter */}
              {(isSuperAdmin || isAdmin) && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Select value={selectedMember?.id || 'all'} onValueChange={handleMemberSelect}>
                    <SelectTrigger className="w-[220px] bg-background/70 backdrop-blur-sm border-border" data-tour="member-filter">
                      <Users className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="View team member..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">My Dashboard</SelectItem>
                      {allUsers.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedMember && (
                    <Button variant="ghost" size="icon" className="bg-background/70 backdrop-blur-sm" onClick={() => handleMemberSelect('all')}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Selected Member Banner */}
      {selectedMember && (
        <Card className="border-primary/30 bg-primary/5 overflow-hidden">
          <CardContent className="p-4 sm:p-6">

            {/* User profile row */}
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center text-xl font-bold text-primary flex-shrink-0">
                {selectedMember.first_name[0]}{selectedMember.last_name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-base sm:text-lg leading-tight">
                  {selectedMember.first_name} {selectedMember.last_name}
                </div>
                <div className="text-sm text-muted-foreground capitalize mt-0.5">
                  {selectedMember.role?.replace('_', ' ')} · {selectedMember.department?.replace('_', ' ')}
                </div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">
                  {selectedMember.email}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-xs text-muted-foreground">Projects Assigned</div>
                <div className="text-3xl font-bold text-primary leading-tight">{memberProjects.length}</div>
              </div>
            </div>

            {/* Project Allocations */}
            {memberProjects.length > 0 && (
              <div className="mt-5 space-y-3">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                  Project Allocations
                </div>
                {memberProjects.map(project => {
                  const alloc = project.allocations?.find(a => a.user_id === selectedMember.id);
                  const isRetainer = project.project_type === 'monthly_retainer' && (project.monthly_budget_hours || 0) > 0;
                  const allocated = alloc?.allocated_hours || 0;
                  const used = isRetainer
                    ? (memberMonthlyProjectHours[project.id] || 0)
                    : (memberProjectHours[project.id] || 0);
                  const scopeLabel = isRetainer ? 'this month' : 'all-time';
                  const remaining = Math.max(0, allocated - used);
                  const isOver = allocated > 0 && used > allocated;
                  const isFull = allocated > 0 && used >= allocated && !isOver;
                  const pct = allocated > 0 ? Math.min(100, (used / allocated) * 100) : 0;
                  const badgeClass = isOver
                    ? 'border-red-400 text-red-600 bg-red-50 dark:bg-red-500/10'
                    : isFull
                    ? 'border-orange-400 text-orange-600 bg-orange-50 dark:bg-orange-500/10'
                    : 'border-green-400 text-green-600 bg-green-50 dark:bg-green-500/10';
                  const badgeLabel = isOver
                    ? `+${(used - allocated).toFixed(1)}h over`
                    : isFull
                    ? 'Fully used'
                    : `${remaining.toFixed(1)}h left`;
                  return (
                    <div key={project.id} className="p-3 bg-background rounded-xl border border-border">
                      <div className="flex items-start justify-between gap-2 mb-2.5">
                        <div className="min-w-0">
                          <span className="font-medium text-sm">{project.name}</span>
                          {project.client_name && (
                            <span className="text-xs text-muted-foreground ml-1.5">({project.client_name})</span>
                          )}
                        </div>
                        <Badge variant="outline" className={`text-xs flex-shrink-0 ${badgeClass}`}>
                          {badgeLabel}
                        </Badge>
                      </div>
                      <Progress
                        value={pct}
                        className={`h-2.5 w-full rounded-full ${
                          isOver
                            ? '[&>div]:bg-red-500'
                            : isFull
                            ? '[&>div]:bg-orange-400'
                            : pct >= 80
                            ? '[&>div]:bg-orange-400'
                            : ''
                        }`}
                      />
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-xs text-muted-foreground font-mono">
                          {used.toFixed(1)}h / {allocated > 0 ? `${allocated.toFixed(1)}h` : 'No allocation'}
                          <span className="ml-1 text-[10px] text-muted-foreground/60">({scopeLabel})</span>
                        </span>
                        {isOver && (isAdmin || isSuperAdmin) && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[11px] text-primary hover:text-primary hover:bg-primary/10 gap-1"
                            onClick={e => { e.stopPropagation(); setEditAllocDialog({ project, alloc, newHours: String(Math.ceil(used)) }); }}
                          >
                            <Pencil className="w-3 h-3" /> Adjust
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dashboard Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" className="gap-2" data-testid="tab-overview">
            <BarChart3 className="w-4 h-4" /> Overview
          </TabsTrigger>
          {!isAdmin && !isSuperAdmin && (
            <TabsTrigger value="worklog" className="gap-2" data-testid="tab-worklog">
              <ClipboardList className="w-4 h-4" /> Work Log
            </TabsTrigger>
          )}
          {canViewReports && (
            <>
              <TabsTrigger value="capacity" className="gap-2" data-testid="tab-capacity">
                <Users className="w-4 h-4" /> Capacity
              </TabsTrigger>
              <TabsTrigger value="burnrate" className="gap-2" data-testid="tab-burnrate">
                <Flame className="w-4 h-4" /> Burn Rate
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-4">
          <OverviewTab
            stats={stats} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} user={user} dailyData={dailyData}
            projectData={projectData} projects={projects}
          />
        </TabsContent>

        {/* Work Log Tab — members only */}
        {!isAdmin && !isSuperAdmin && (
          <TabsContent value="worklog" className="mt-4">
            {activeTab === 'worklog' && <WorkLogTab api={api} />}
          </TabsContent>
        )}

        {/* Capacity Tab */}
        {canViewReports && (
          <TabsContent value="capacity" className="space-y-6 mt-4 overflow-x-hidden">
            <CapacityTab data={capacityData} />
          </TabsContent>
        )}

        {/* Burn Rate Tab */}
        {canViewReports && (
          <TabsContent value="burnrate" className="space-y-6 mt-4 overflow-x-hidden">
            <BurnRateTab data={burnData} />
          </TabsContent>
        )}
      </Tabs>

      {/* Edit Allocation Dialog — admin only */}
      <Dialog open={!!editAllocDialog} onOpenChange={open => !open && setEditAllocDialog(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Adjust Allocation</DialogTitle>
          </DialogHeader>
          {editAllocDialog && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                Update <span className="font-medium text-foreground">{selectedMember?.first_name} {selectedMember?.last_name}</span>'s
                allocated hours on <span className="font-medium text-foreground">{editAllocDialog.project.name}</span>.
                They have already logged <span className="font-medium text-foreground">{(memberProjectHours[editAllocDialog.project.id] || 0).toFixed(1)}h</span>.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="alloc-hours">New Allocated Hours</Label>
                <Input
                  id="alloc-hours"
                  type="number"
                  min={Math.ceil(memberProjectHours[editAllocDialog.project.id] || 0)}
                  step={1}
                  value={editAllocDialog.newHours}
                  onChange={e => setEditAllocDialog(d => ({ ...d, newHours: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && saveAllocation()}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">Minimum {Math.ceil(memberProjectHours[editAllocDialog.project.id] || 0)}h to clear the over-allocation.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAllocDialog(null)} disabled={editAllocSaving}>Cancel</Button>
            <Button onClick={saveAllocation} disabled={editAllocSaving}>
              {editAllocSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* =================== OVERVIEW TAB =================== */
const OverviewTab = ({ stats, isAdmin, isSuperAdmin, user, dailyData, projectData, projects }) => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        icon={Clock} iconColor="text-primary" iconBg="bg-primary/10"
        label="This Week" value={`${parseFloat((stats?.week_hours || 0).toFixed(2))}h`}
        sub="of 40h target" testId="week-hours-card"
        progress={(stats?.week_hours / 40) * 100}
      />
      <StatCard
        icon={TrendingUp} iconColor="text-green-500" iconBg="bg-green-500/10"
        label="Billable" value={`${parseFloat((stats?.week_billable_hours || 0).toFixed(2))}h`}
        sub={`${stats?.week_hours > 0 ? Math.round((stats?.week_billable_hours / stats?.week_hours) * 100) : 0}% of logged time`}
        testId="billable-hours-card"
      />
      <StatCard
        icon={Briefcase} iconColor="text-purple-500" iconBg="bg-purple-500/10"
        label="Projects" value={stats?.active_projects || 0}
        sub="Active assignments" testId="projects-card"
      />
      {isAdmin ? (
        <StatCard
          icon={stats?.pending_approvals > 0 ? AlertTriangle : CheckCircle}
          iconColor={stats?.pending_approvals > 0 ? 'text-orange-500' : 'text-green-500'}
          iconBg={stats?.pending_approvals > 0 ? 'bg-orange-500/10' : 'bg-green-500/10'}
          label="Approvals" value={stats?.pending_approvals || 0}
          sub="Pending review" testId="pending-approvals-card"
        />
      ) : (
        <StatCard
          icon={Users} iconColor="text-blue-500" iconBg="bg-blue-500/10"
          label="Team" value={stats?.team_count || 1}
          sub="Team members" testId="team-count-card"
        />
      )}
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card data-testid="daily-hours-chart">
        <CardHeader><CardTitle className="text-lg">Daily Hours This Week</CardTitle></CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyData}>
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(v) => `${parseFloat(v).toFixed(2)}h`} />
                <Bar dataKey="total" name="Total" fill="hsl(221, 83%, 53%)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="billable" name="Billable" fill="hsl(142, 71%, 45%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="project-breakdown-chart">
        <CardHeader><CardTitle className="text-lg">Hours by Project</CardTitle></CardHeader>
        <CardContent>
          {projectData.length > 0 ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="h-52 w-full sm:w-[60%] sm:h-64 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={projectData} dataKey="hours" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2}>
                      {projectData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(v) => `${parseFloat(v).toFixed(2)}h`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 flex-1 min-w-0">
                {projectData.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm min-w-0">
                    <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: p.fill }} />
                    <span className="text-muted-foreground truncate flex-1 min-w-0">{p.name}</span>
                    <span className="font-medium shrink-0">{Math.round((p.hours || 0) * 10) / 10}h</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">No time logged this week</div>
          )}
        </CardContent>
      </Card>
    </div>

    {/* Active Projects */}
    <Card data-testid="recent-projects">
      <CardHeader><CardTitle className="text-lg">Active Projects</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-3">
          {projects.map((project) => {
            const userAlloc = (!isAdmin && !isSuperAdmin) ? (project.allocations || []).find(a => a.user_id === user?.id) : null;
            const displayBudget = userAlloc ? (userAlloc.allocated_hours || 0) : (project.budgeted_hours || 0);
            return (
              <div key={project.id} className="flex items-center justify-between p-4 border border-border rounded-lg hover:border-primary/30 transition-colors">
                <div className="flex items-center gap-4">
                  <div className={`w-2 h-10 rounded-full ${project.is_billable ? 'bg-primary' : 'bg-muted-foreground'}`} />
                  <div>
                    <div className="font-medium">{project.name}</div>
                    <div className="text-sm text-muted-foreground">{project.client_name || 'Internal Project'}</div>
                  </div>
                </div>
                <div className="text-right">
                  {(() => {
                    const isRetainer = project.project_type === 'monthly_retainer' && (project.monthly_budget_hours || 0) > 0;
                    let displayHours;
                    if (!userAlloc) {
                      displayHours = project.total_logged_hours || 0;
                    } else if (isRetainer) {
                      displayHours = project.my_monthly_logged_hours || 0;
                    } else {
                      displayHours = project.my_logged_hours || 0;
                    }
                    return (
                      <>
                        <div className="font-mono text-sm">{Math.round(displayHours * 10) / 10}h / {Math.round(displayBudget * 10) / 10}h</div>
                        <Progress value={displayBudget > 0 ? Math.min((displayHours / displayBudget) * 100, 100) : 0} className="w-24 h-1.5 mt-1" />
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })}
          {projects.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">No active projects assigned</div>
          )}
        </div>
      </CardContent>
    </Card>
  </>
);

/* =================== STAT CARD =================== */
const StatCard = ({ icon: Icon, iconColor, iconBg, label, value, sub, testId, progress }) => (
  <Card className="card-hover rounded-xl" data-testid={testId}>
    <CardContent className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-3xl font-bold mb-1">{value}</div>
      <div className="text-sm text-muted-foreground">{sub}</div>
      {progress !== undefined && <Progress value={Math.min(progress, 100)} className="mt-3 h-1.5" />}
    </CardContent>
  </Card>
);

/* =================== CAPACITY TAB =================== */
const CapacityTab = ({ data }) => {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const deptChartData = data.department_summary.map((d, i) => ({
    name: d.department.replace('_', ' '),
    allocated: d.total_allocated,
    available: d.total_available,
    fill: CHART_COLORS[i % CHART_COLORS.length],
  }));

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users} iconColor="text-primary" iconBg="bg-primary/10"
          label="Team Size" value={data.total_team_size} sub="Active members"
          testId="capacity-team-size"
        />
        <StatCard
          icon={Activity} iconColor="text-blue-500" iconBg="bg-blue-500/10"
          label="Avg Utilization" value={`${data.avg_utilization}%`} sub="Across team"
          testId="capacity-avg-util" progress={data.avg_utilization}
        />
        <StatCard
          icon={ArrowUpRight} iconColor="text-red-500" iconBg="bg-red-500/10"
          label="Over-Allocated" value={data.over_allocated_count} sub="Need rebalancing"
          testId="capacity-over-allocated"
        />
        <StatCard
          icon={ArrowDownRight} iconColor="text-amber-500" iconBg="bg-amber-500/10"
          label="Under-Utilized" value={data.under_utilized_count} sub="Below 50% capacity"
          testId="capacity-under-utilized"
        />
      </div>

      {/* Department Chart */}
      <Card data-testid="capacity-dept-chart">
        <CardHeader><CardTitle className="text-lg">Department Capacity</CardTitle></CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={deptChartData}>
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { fill: 'hsl(var(--muted-foreground))' } }} />
                <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                <Bar dataKey="available" name="Available" fill="hsl(var(--muted))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="allocated" name="Allocated" fill="hsl(221, 83%, 53%)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Team Member List */}
      <Card data-testid="capacity-team-list">
        <CardHeader><CardTitle className="text-lg">Team Allocation</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data.team_capacity.map((member) => {
              const utilColor = member.is_over_allocated ? 'text-red-500' : member.utilization_pct < 50 ? 'text-amber-500' : 'text-primary';
              const barClass = member.is_over_allocated ? '[&>div]:bg-red-500' : '';
              return (
                <div key={member.user_id}>
                  {/* Mobile card */}
                  <div className="sm:hidden p-3 border border-border rounded-lg space-y-2 overflow-hidden">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary flex-shrink-0">
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="w-0 flex-1 overflow-hidden">
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-medium break-words min-w-0 flex-1">{member.name}</span>
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            <span className={`text-lg font-bold ${utilColor}`}>{member.utilization_pct}%</span>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-1 mt-0.5">
                          <Badge variant="outline" className="text-xs capitalize">{member.department.replace('_', ' ')}</Badge>
                          {member.is_over_allocated && (
                            <Badge className="bg-red-500/10 text-red-600 text-xs border-0">Over-allocated</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Progress value={Math.min(member.utilization_pct, 100)} className={`h-2 ${barClass}`} />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="font-mono">{member.total_allocated}h / {member.total_available}h</span>
                      <span>{member.project_count} projects</span>
                    </div>
                  </div>

                  {/* Desktop row */}
                  <div className="hidden sm:flex items-center gap-4 p-3 border border-border rounded-lg">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-sm font-bold text-primary">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{member.name}</span>
                        <Badge variant="outline" className="text-xs capitalize">{member.department.replace('_', ' ')}</Badge>
                        {member.is_over_allocated && (
                          <Badge className="bg-red-500/10 text-red-600 text-xs">Over-allocated</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Progress value={Math.min(member.utilization_pct, 100)} className={`flex-1 h-2 ${barClass}`} />
                        <span className="text-xs font-mono text-muted-foreground w-16 text-right">
                          {member.total_allocated}h / {member.total_available}h
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-bold ${utilColor}`}>{member.utilization_pct}%</div>
                      <div className="text-xs text-muted-foreground">{member.project_count} projects</div>
                    </div>
                  </div>
                </div>
              );
            })}
            {data.team_capacity.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">No team members found</div>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
};

/* =================== BURN RATE TAB =================== */
const BurnRateTab = ({ data }) => {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const summary = data.summary;
  const statusColors = {
    on_track: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'On Track' },
    over_burning: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Over-burning' },
    under_burning: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: 'Under-burning' },
    budget_exhausted: { bg: 'bg-red-500/10', text: 'text-red-700', label: 'Budget Exhausted' },
  };

  const burnChartData = data.projects.slice(0, 8).map(p => ({
    name: p.project_name,
    burned: p.burn_pct,
    time_elapsed: p.time_elapsed_pct,
  }));

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Flame} iconColor="text-primary" iconBg="bg-primary/10"
          label="Total Budgeted" value={`${summary.total_budgeted}h`} sub={`${summary.total_logged}h logged`}
          testId="burn-total-budgeted"
          progress={summary.total_budgeted > 0 ? (summary.total_logged / summary.total_budgeted) * 100 : 0}
        />
        <StatCard
          icon={CheckCircle} iconColor="text-green-500" iconBg="bg-green-500/10"
          label="On Track" value={summary.on_track} sub="Projects on schedule"
          testId="burn-on-track"
        />
        <StatCard
          icon={AlertTriangle} iconColor="text-red-500" iconBg="bg-red-500/10"
          label="At Risk" value={summary.over_burning + summary.budget_exhausted} sub="Need attention"
          testId="burn-at-risk"
        />
        <StatCard
          icon={TrendingUp} iconColor="text-blue-500" iconBg="bg-blue-500/10"
          label="Revenue Potential" value={`₹${(summary.total_revenue_potential / 1000).toFixed(1)}k`}
          sub="From remaining hours" testId="burn-revenue"
        />
      </div>

      {/* Burn vs Time Chart */}
      {burnChartData.length > 0 && (
        <Card data-testid="burn-rate-chart" className="overflow-hidden">
          <CardHeader><CardTitle className="text-lg">Budget Burn vs Time Elapsed</CardTitle></CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={burnChartData} layout="vertical" margin={{ left: 8, right: 10 }}>
                  <XAxis type="number" domain={[0, 120]} stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={v => `${v}%`} />
                  <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} width={180} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={v => `${v}%`} />
                  <Bar dataKey="burned" name="Budget Burned" fill="hsl(221, 83%, 53%)" radius={[0, 4, 4, 0]} barSize={14} />
                  <Bar dataKey="time_elapsed" name="Time Elapsed" fill="hsl(var(--muted-foreground))" radius={[0, 4, 4, 0]} barSize={14} opacity={0.4} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Project List */}
      <Card data-testid="burn-rate-projects" className="overflow-hidden">
        <CardHeader><CardTitle className="text-lg">Project Burn Details</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data.projects.map((project) => {
              const sc = statusColors[project.status] || statusColors.on_track;
              const barColor = project.status === 'over_burning' || project.status === 'budget_exhausted'
                ? 'bg-red-500' : project.status === 'under_burning' ? 'bg-amber-500' : 'bg-primary';
              const burnedColor = project.status === 'over_burning' || project.status === 'budget_exhausted'
                ? 'text-red-500' : project.status === 'under_burning' ? 'text-amber-500' : 'text-foreground';
              const initials = project.project_name.split(/[\s_]+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
              const progressBar = (
                <div className="h-2 bg-muted rounded-full overflow-hidden relative">
                  <div className="absolute top-0 left-0 h-full bg-muted-foreground/20 rounded-full" style={{ width: `${Math.min(project.time_elapsed_pct, 100)}%` }} />
                  <div className={`absolute top-0 left-0 h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(project.burn_pct, 100)}%` }} />
                </div>
              );
              return (
                <div key={project.project_id}>
                  {/* Mobile card */}
                  <div className="sm:hidden p-3 border border-border rounded-lg space-y-2 overflow-hidden">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center text-sm font-bold text-primary flex-shrink-0">
                        {initials}
                      </div>
                      <div className="w-0 flex-1 overflow-hidden">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-medium break-words min-w-0 flex-1">{project.project_name}</div>
                          <div className="font-mono text-xs text-muted-foreground flex-shrink-0 pl-1">{project.weekly_burn_rate}h/wk</div>
                        </div>
                        {project.client_name && <div className="text-xs text-muted-foreground truncate mt-0.5">({project.client_name})</div>}
                        <div className="flex flex-wrap gap-1 mt-1">
                          <Badge className={`${sc.bg} ${sc.text} text-xs border-0`}>{sc.label}</Badge>
                          {project.project_type === 'monthly_retainer' && <Badge className="bg-blue-500/20 text-blue-600 text-xs border-0">Monthly Retainer</Badge>}
                          {project.project_type === 'time_and_material' && <Badge className="bg-purple-500/20 text-purple-600 text-xs border-0">T&M</Badge>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Budget</span>
                      <div className="text-right">
                        <div className="font-medium">{parseFloat((project.remaining_hours || 0).toFixed(1))}h left</div>
                        <div className="text-muted-foreground">{project.days_remaining}d remaining</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <div className="font-mono text-muted-foreground truncate flex-1 min-w-0">
                        {Math.round((project.logged_hours || 0) * 10) / 10}h / {Math.round((project.budgeted_hours || 0) * 10) / 10}h
                      </div>
                      <div className={`font-medium flex-shrink-0 ${burnedColor}`}>{project.burn_pct}% burned</div>
                    </div>
                    {progressBar}
                    {project.forecast_completion && (
                      <div className="space-y-1.5 pt-0.5">
                        <div className="flex items-center gap-2 text-xs">
                          <div className="w-6 h-6 bg-blue-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <Calendar className="w-3.5 h-3.5 text-blue-500" />
                          </div>
                          <div>
                            <div className="text-muted-foreground">Forecasted completion</div>
                            <div className="font-medium text-foreground">{project.forecast_completion}</div>
                          </div>
                        </div>
                        {project.revenue_potential > 0 && (
                          <div className="flex items-center gap-2 text-xs">
                            <div className="w-6 h-6 bg-green-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                              <DollarSign className="w-3.5 h-3.5 text-green-500" />
                            </div>
                            <div>
                              <div className="text-muted-foreground">Revenue potential</div>
                              <div className="font-medium text-green-600">₹{project.revenue_potential.toLocaleString()}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Desktop card */}
                  <div className="hidden sm:block p-4 border border-border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{project.project_name}</span>
                        {project.client_name && <span className="text-sm text-muted-foreground">({project.client_name})</span>}
                        <Badge className={`${sc.bg} ${sc.text} text-xs`}>{sc.label}</Badge>
                        {project.project_type === 'monthly_retainer' && <Badge className="bg-blue-500/20 text-blue-600 text-xs border-0">Monthly Retainer</Badge>}
                        {project.project_type === 'time_and_material' && <Badge className="bg-purple-500/20 text-purple-600 text-xs border-0">T&M</Badge>}
                        {(!project.project_type || project.project_type === 'fixed_price') && <Badge className="bg-orange-500/20 text-orange-600 text-xs border-0">Fixed Price</Badge>}
                      </div>
                      <div className="text-right">
                        <span className="font-mono text-sm">{project.weekly_burn_rate}h/wk</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                          <span>Budget: {Math.round((project.logged_hours || 0) * 10) / 10}h / {Math.round((project.budgeted_hours || 0) * 10) / 10}h</span>
                          <span>{project.burn_pct}% burned</span>
                        </div>
                        {progressBar}
                      </div>
                      <div className="w-28 text-right text-sm">
                        <div className="font-mono">{parseFloat((project.remaining_hours || 0).toFixed(2))}h left</div>
                        <div className="text-xs text-muted-foreground">{project.days_remaining}d remaining</div>
                      </div>
                    </div>
                    {project.forecast_completion && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        Forecasted completion: <span className="font-medium">{project.forecast_completion}</span>
                        {project.revenue_potential > 0 && (
                          <span className="ml-3">Revenue potential: <span className="font-medium text-green-600">₹{project.revenue_potential.toLocaleString()}</span></span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {data.projects.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">No active projects to analyze</div>
            )}
          </div>
        </CardContent>
      </Card>

    </>
  );
};

/* =================== WORK LOG TAB =================== */
const toHHMM = (decimalHours) => {
  const h = Math.floor(decimalHours);
  const m = Math.round((decimalHours - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const WorkLogTab = ({ api }) => {
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const today = now.toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [entries, setEntries] = useState([]);
  const [projectMap, setProjectMap] = useState({});
  const [workTypeMap, setWorkTypeMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);

  const fetchData = async (from, to) => {
    setLoading(true);
    try {
      const [entriesRes, projectsRes, workTypesRes] = await Promise.all([
        api.get(`/time-entries?date_from=${from}&date_to=${to}`),
        api.get('/projects'),
        api.get('/service-catalog/work-types'),
      ]);
      const pMap = {};
      for (const p of projectsRes.data) pMap[p.id] = p;
      const wMap = {};
      for (const w of workTypesRes.data) wMap[w.id] = w.name;
      setEntries([...entriesRes.data].sort((a, b) => b.date.localeCompare(a.date)));
      setProjectMap(pMap);
      setWorkTypeMap(wMap);
      setHasData(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(fromDate, toDate); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApply = () => {
    if (fromDate && toDate && fromDate <= toDate) fetchData(fromDate, toDate);
  };

  const totalHours = entries.reduce((s, e) => s + (e.hours || 0), 0);

  // Per-day subtotals for group headers
  const dayTotals = {};
  for (const e of entries) dayTotals[e.date] = (dayTotals[e.date] || 0) + (e.hours || 0);

  // Track which date headers we've already rendered
  const renderedDates = new Set();

  return (
    <div className="space-y-4">
      {/* Date range filter bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">From</label>
              <input
                type="date"
                value={fromDate}
                max={toDate}
                onChange={e => setFromDate(e.target.value)}
                className="h-9 border border-border rounded-md px-3 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">To</label>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                max={today}
                onChange={e => setToDate(e.target.value)}
                className="h-9 border border-border rounded-md px-3 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <Button
              onClick={handleApply}
              disabled={loading || !fromDate || !toDate || fromDate > toDate}
              className="h-9"
            >
              {loading ? 'Loading…' : 'Apply'}
            </Button>
            <button
              onClick={() => { setFromDate(monthStart); setToDate(today); fetchData(monthStart, today); }}
              className="h-9 px-3 text-sm rounded-md border border-border bg-background hover:bg-muted transition-colors"
            >
              This Month
            </button>

            {hasData && !loading && (
              <div className="ml-auto flex items-center gap-5">
                <div className="text-center">
                  <div className="font-mono font-bold text-lg leading-tight">{toHHMM(totalHours)}</div>
                  <div className="text-xs text-muted-foreground">Total</div>
                </div>
                <div className="text-center">
                  <div className="font-mono font-bold text-lg leading-tight">{entries.length}</div>
                  <div className="text-xs text-muted-foreground">Entries</div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-52">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium">No time entries found</p>
              <p className="text-xs mt-1 text-muted-foreground/60">No work logged in this date range</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-32">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Project</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">Description</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell w-32">Task Type</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-20">Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => {
                    const project = projectMap[entry.project_id];
                    const isBillable = entry.is_billable !== false;
                    const isNewDate = !renderedDates.has(entry.date);
                    if (isNewDate) renderedDates.add(entry.date);

                    return (
                      <React.Fragment key={entry.id}>
                        {isNewDate && (
                          <tr className="bg-muted/20 border-t border-border">
                            <td colSpan={6} className="px-4 py-2">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-foreground">
                                  {new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', {
                                    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                                  })}
                                </span>
                                <span className="text-xs font-mono font-semibold text-muted-foreground">
                                  {toHHMM(dayTotals[entry.date])}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}
                        <tr className="border-t border-border/50 hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap" />
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isBillable ? 'bg-primary' : 'bg-muted-foreground/40'}`} />
                              <div className="min-w-0">
                                <div className="font-medium leading-tight">{project?.name || 'Unknown Project'}</div>
                                {project?.client_name && (
                                  <div className="text-xs text-muted-foreground mt-0.5 truncate">{project.client_name}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell max-w-xs">
                            {entry.description
                              ? <span className="text-muted-foreground line-clamp-2 leading-snug">{entry.description}</span>
                              : <span className="text-muted-foreground/40 italic text-xs">—</span>
                            }
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            {workTypeMap[entry.work_type_id]
                              ? <span className="px-2 py-0.5 rounded-full text-xs border border-border bg-muted/50 text-muted-foreground whitespace-nowrap">{workTypeMap[entry.work_type_id]}</span>
                              : <span className="text-muted-foreground/40 text-xs">—</span>
                            }
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-mono font-semibold tabular-nums">{toHHMM(entry.hours || 0)}</span>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border bg-muted/30">
                    <td className="px-4 py-3 text-xs font-semibold text-muted-foreground" colSpan={2}>
                      {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                    </td>
                    <td className="hidden md:table-cell" />
                    <td className="hidden lg:table-cell" />
                    <td className="px-4 py-3 text-right font-mono font-bold">{toHHMM(totalHours)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
